import type { USER_INFO_SHORT, USER_PROFILE } from '@/views/screens/Profile/types';
import { DEFAULT_LANGUAGE } from '../../contants/common';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import AuthTypes from './AuthTypes';

export interface AuthState extends Record<string, any> {
  user: DefaultStoreObject<USER_INFO_SHORT> & {
    loginStatus: boolean;
    language: string;
  };
  userProfile: DefaultStoreObject<USER_PROFILE>;
}

const initialState: AuthState = {
  user: {
    ...DEFAULT_STORE_OBJECT,
    loginStatus: false,
    language: DEFAULT_LANGUAGE,
    data: null,
  },
  menus: { ...DEFAULT_STORE_OBJECT },
  menusItems: { ...DEFAULT_STORE_OBJECT },
  selectedCompany: false,
  forgotPassword: { ...DEFAULT_STORE_OBJECT },
  userRegistration: { ...DEFAULT_STORE_OBJECT },
  userProfile: { ...DEFAULT_STORE_OBJECT },
  updateProfileData: { ...DEFAULT_STORE_OBJECT },
  updatePassword: { ...DEFAULT_STORE_OBJECT },
  updateUserPreference: { ...DEFAULT_STORE_OBJECT },
  getUserPreference: { ...DEFAULT_STORE_OBJECT, data: {} },
  changeCompany: { ...DEFAULT_STORE_OBJECT },
  approveRejectShipment: { ...DEFAULT_STORE_OBJECT },
  updateUserProfileImage: { ...DEFAULT_STORE_OBJECT },
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const AuthReducer = (state = initialState, { type, payload }: any): AuthState => {
  switch (type) {
    case AuthTypes.LOGIN_IN_PROGRESS: {
      return {
        ...state,
        user: {
          ...DEFAULT_STORE_OBJECT,
          loginStatus: false,
          progressing: true,
          ...payload,
        },
        selectedCompany: false,
      };
    }

    case AuthTypes.LOGIN_IN_SUCCESS: {
      return {
        ...state,
        user: {
          ...DEFAULT_STORE_OBJECT,
          loginStatus: true,
          success: true,
          ...payload,
        },
      };
    }

    case AuthTypes.LOGIN_IN_ERROR: {
      return {
        ...state,
        user: {
          ...DEFAULT_STORE_OBJECT,
          loginStatus: false,
          error: true,
          ...payload,
        },
        selectedCompany: false,
      };
    }

    case AuthTypes.GET_USER_MENU_PROGRESS: {
      return {
        ...state,
        menus: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_MENU_SUCCESS: {
      return {
        ...state,
        menus: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_MENU_ERROR: {
      return {
        ...state,
        menus: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_MENU_ITEMS_PROGRESS: {
      return {
        ...state,
        menusItems: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_MENU_ITEMS_SUCCESS: {
      return {
        ...state,
        menusItems: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_MENU_ITEMS_ERROR: {
      return {
        ...state,
        menusItems: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.SET_SELECTED_COMPANY: {
      localStorage.setItem('selectedCompanyId', payload?.companyId || '');
      return {
        ...state,
        selectedCompany: payload,
      };
    }

    case AuthTypes.INITIATE_LOGOUT: {
      localStorage.clear();
      return {
        ...state,
        user: { ...state.user, loginStatus: false, data: null },
      };
    }

    case AuthTypes.FORGOT_PASSWORD_PROGRESS: {
      return {
        ...state,
        forgotPassword: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }

    case AuthTypes.FORGOT_PASSWORD_SUCCESS: {
      return {
        ...state,
        forgotPassword: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case AuthTypes.FORGOT_PASSWORD_ERROR: {
      return {
        ...state,
        forgotPassword: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.RESET_FORGOT_PASSWORD: {
      return {
        ...state,
        forgotPassword: {
          ...DEFAULT_STORE_OBJECT,
        },
      };
    }
    case AuthTypes.USER_REGISTRATION_PROGRESS: {
      return {
        ...state,
        userRegistration: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }

    case AuthTypes.USER_REGISTRATION_SUCCESS: {
      return {
        ...state,
        userRegistration: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case AuthTypes.USER_REGISTRATION_ERROR: {
      return {
        ...state,
        userRegistration: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.RESET_USER_REGISTRATION: {
      return {
        ...state,
        userRegistration: {
          ...DEFAULT_STORE_OBJECT,
        },
      };
    }

    case AuthTypes.GET_USER_PROFILE_PROGRESS: {
      return {
        ...state,
        userProfile: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_PROFILE_SUCCESS: {
      return {
        ...state,
        userProfile: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_PROFILE_ERROR: {
      return {
        ...state,
        userProfile: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.USER_PROFILE_UPDATE_PROGRESS: {
      return {
        ...state,
        updateProfileData: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }

    case AuthTypes.USER_PROFILE_UPDATE_SUCCESS: {
      return {
        ...state,
        updateProfileData: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }

    case AuthTypes.USER_PROFILE_UPDATE_ERROR: {
      return {
        ...state,
        updateProfileData: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.UPDATE_USER_PASSWORD_PROGRESS: {
      return {
        ...state,
        updatePassword: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }
    case AuthTypes.UPDATE_USER_PASSWORD_SUCCESS: {
      return {
        ...state,
        updatePassword: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case AuthTypes.UPDATE_USER_PASSWORD_ERROR: {
      return {
        ...state,
        updatePassword: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.UPDATE_USER_PREFERENCE_PROGRESS: {
      return {
        ...state,
        updateUserPreference: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }
    case AuthTypes.UPDATE_USER_PREFERENCE_SUCCESS: {
      return {
        ...state,
        updateUserPreference: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case AuthTypes.UPDATE_USER_PREFERENCE_ERROR: {
      return {
        ...state,
        updateUserPreference: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.GET_USER_PREFERENCE_PROGRESS: {
      return {
        ...state,
        getUserPreference: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }
    case AuthTypes.GET_USER_PREFERENCE_SUCCESS: {
      return {
        ...state,
        getUserPreference: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case AuthTypes.GET_USER_PREFERENCE_ERROR: {
      return {
        ...state,
        getUserPreference: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.CHANGE_COMPANY_IN_PROGRESS: {
      return {
        ...state,
        changeCompany: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }
    case AuthTypes.CHANGE_COMPANY_IN_SUCCESS: {
      return {
        ...state,
        changeCompany: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case AuthTypes.CHANGE_COMPANY_IN_FAIL: {
      return {
        ...state,
        changeCompany: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.APPROVE_REJECT_SHIPMENT_IN_PROGRESS: {
      return {
        ...state,
        approveRejectShipment: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }
    case AuthTypes.APPROVE_REJECT_SHIPMENT_IN_SUCCESS: {
      return {
        ...state,
        approveRejectShipment: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case AuthTypes.APPROVE_REJECT_SHIPMENT_IN_FAIL: {
      return {
        ...state,
        approveRejectShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case AuthTypes.UPDATE_USER_PROFILE_IMAGE_PROGRESS: {
      return {
        ...state,
        updateUserProfileImage: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          ...payload,
        },
      };
    }
    case AuthTypes.UPDATE_USER_PROFILE_IMAGE_SUCCESS: {
      return {
        ...state,
        updateUserProfileImage: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
        userProfile: {
          ...state.userProfile,
          data: {
            ...((state.userProfile.data as USER_PROFILE) || {}),
            profileImage: payload.profileImage,
          },
        },
      };
    }
    case AuthTypes.UPDATE_USER_PROFILE_IMAGE_ERROR: {
      return {
        ...state,
        updateUserProfileImage: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    default:
      return state;
  }
};

export default AuthReducer;
